import type { ComponentType } from 'react';

import type { FurnitureType } from '@pomoshnik/shared';

import { BalconySVG } from './visualizations/BalconySVG';
import { BathroomSVG } from './visualizations/BathroomSVG';
import { BedroomSVG } from './visualizations/BedroomSVG';
import { ChildrenSVG } from './visualizations/ChildrenSVG';
import { HallwaySVG } from './visualizations/HallwaySVG';
import { KitchenSVG } from './visualizations/KitchenSVG';
import { LivingRoomSVG } from './visualizations/LivingRoomSVG';
import { OtherSVG } from './visualizations/OtherSVG';
import { WardrobeSVG } from './visualizations/WardrobeSVG';
import type { VisualizationProps } from './visualizations/types';

/**
 * Wrapper над 9 SVG-визуализациями (Block 6 Phase 2 Step 1, ADR-0015 Phase 2).
 * Маршрутизирует props в нужный SVG по `FurnitureType` через `VISUALIZATION_MAP`
 * (паттерн зеркальный `ICON_MAP` в `TypePickerScreen.tsx`). Exhaustive switch
 * invariant test ловит drift между shared vocab и UI сразу.
 *
 * `ConfiguratorScreen.tsx` (Step 2) использует этот компонент: передаёт
 * `type` из URL params + 4-5 цветов из `slotResolver.ts`.
 */
const VISUALIZATION_MAP: Record<FurnitureType, ComponentType<VisualizationProps>> = {
  kitchen: KitchenSVG,
  hallway: HallwaySVG,
  wardrobe: WardrobeSVG,
  bedroom: BedroomSVG,
  living_room: LivingRoomSVG,
  children: ChildrenSVG,
  bathroom: BathroomSVG,
  balcony: BalconySVG,
  other: OtherSVG,
};

export interface ConfiguratorVisualizationProps extends VisualizationProps {
  type: FurnitureType;
}

export function ConfiguratorVisualization({ type, ...colors }: ConfiguratorVisualizationProps) {
  const Component = VISUALIZATION_MAP[type];
  return <Component {...colors} />;
}

/**
 * Test-only export для exhaustive switch invariant. Зеркало `__INTERNAL_ICON_MAP`
 * из TypePickerScreen.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const __INTERNAL_VISUALIZATION_MAP = VISUALIZATION_MAP;
